'use client';

import { AuthApi } from '@/apis/auth';
import { hydrateUserProfileFromApi } from '@/providers/auth/hydrate-user-profile';
import { resetUserProfileHydration } from '@/providers/auth/user-profile-hydration-state';
import { showToast } from '@/providers/root/toast';
import { useUserInfoStore } from '@/store/user/slice';
import { getTouristDeviceIdForRequest, persistServerDeviceId } from '@/utils/device/server-device-id';
import { setSessionReadyCookie } from '@/utils/fetch/session-ready-cookie';
import { syncArmsConfig } from '@/utils/sync-arms-config';

function readBusinessCodeFromFetchError(error: unknown): number | undefined {
    if (!error || typeof error !== 'object' || !('data' in error)) {
        return undefined;
    }
    const payload = (error as { data?: { code?: number } }).data;
    return typeof payload?.code === 'number' ? payload.code : undefined;
}

/**
 * 使用 Firebase ID Token 调 /auth/login/v1（游客转正式 / 三方登录），并写入用户 store。
 */
export async function loginWithFirebaseIdToken(idToken: string): Promise<{ isGuestUpgrade: boolean }> {
    const device_id = await getTouristDeviceIdForRequest();
    if (!device_id) {
        throw new Error('device_id_missing');
    }

    try {
        const res = await AuthApi.postLogin({
            device_id,
            firebase_id_token: idToken,
        });

        if (res.data?.device_id) {
            persistServerDeviceId(res.data.device_id);
        }

        if (!res.data?.user_id) {
            throw new Error(res.message || 'login_failed');
        }

        setSessionReadyCookie();

        const { user_id, login_source } = res.data;
        resetUserProfileHydration();
        useUserInfoStore.getState().updateUserInfo({
            user_id,
            login_source: login_source ?? 0,
        });
        useUserInfoStore.getState().setIsLogin(true);

        syncArmsConfig();

        showToast('Login success');

        await hydrateUserProfileFromApi();

        return {
            isGuestUpgrade: !!res.data.is_guest_upgrade,
        };
    } catch (error: unknown) {
        if (error instanceof Error && 'businessCode' in error) {
            throw error;
        }
        const code = readBusinessCodeFromFetchError(error);
        const err = new Error('login_failed') as Error & { businessCode?: number };
        err.businessCode = code;
        throw err;
    }
}
